import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { BehaviorSubject, catchError, EMPTY, map, Observable, of, tap } from 'rxjs'
import { SessionStorageService } from 'src/app/login/services/session-storage.service'
import { SelectedInstallation } from 'src/app/shared/models/selected-installation.model'
import { ObservableCacheService } from 'src/app/shared/services/observable-cache.service'
import {
    PrivateProfileService as ApiPrivateProfileService,
    EditPrivateProfilePrimaryContactRequest,
    PrivateProfileCustomerInstallationDetailsResponse,
    ProfileListItem,
    UpdateCustomerCprRequest,
    UpdateDigitalPostConsentRequest,
    UpdateNemkontoStatusRequest,
} from '../../../shared/services/backend'
import { RedirectTo } from '../../../utils/redirect-to'

@Injectable()
export class PrivateProfileService {
    private errors: BehaviorSubject<any> = new BehaviorSubject<any>(null)
    public errors$: Observable<any> = this.errors.asObservable()

    // Cache configuration
    private readonly CACHE_DURATION_MS = 15 * 60 * 1000 // 15 minutes

    constructor(
        private sessionStorageService: SessionStorageService,
        private profileService: ApiPrivateProfileService,
        private router: Router,
        private cache: ObservableCacheService
    ) {}

    public setSelectedInstallation(installation: SelectedInstallation) {
        this.sessionStorageService.setSelectedInstallation(installation)
    }

    public getInstallations(forceRefresh: boolean = false): Observable<Array<ProfileListItem>> {
        const cacheKey = 'private-installations'
        const shouldForceRefresh = forceRefresh || this.cache.clearIfStoredValueIsEmptyArray(cacheKey)

        return this.cache.getOrSet(
            cacheKey,
            () =>
                this.profileService.apiProfilePrivateGet().pipe(
                    map(
                        (response) =>
                            response.items.map((item) => ({
                                customerNumber: item.customerNumber,
                                installations: item.installations,
                            })) as ProfileListItem[]
                    ),
                    catchError((error: any) => {
                        this.handleError(error)
                        // Clear cache on error
                        this.cache.clear(cacheKey)
                        return EMPTY
                    })
                ),
            { forceRefresh: shouldForceRefresh, cacheDuration: this.CACHE_DURATION_MS }
        )
    }

    /**
     * Clears the installations cache, forcing the next call to fetch fresh data
     */
    public clearInstallationsCache(): void {
        this.cache.clear('private-installations')
    }

    public getAssociatedUsersInformation(
        customerNumber: string,
        installationNumber: string,
        redirectOnError: boolean = true,
        forceRefresh: boolean = false
    ): Observable<PrivateProfileCustomerInstallationDetailsResponse | null> {
        const cacheKey = this.cache.createKey('private-associatedUsers', customerNumber, installationNumber)

        return this.cache.getOrSet(
            cacheKey,
            () =>
                this.profileService
                    .apiProfilePrivateCustomerCustomerNumberInstallationInstallationNumberGet(
                        customerNumber,
                        installationNumber
                    )
                    .pipe(
                        catchError((error: any) => {
                            if (redirectOnError) {
                                this.handleError(error)
                            }
                            // Remove failed cache entry
                            this.cache.clear(cacheKey)
                            return of(null)
                        })
                    ),
            { forceRefresh, cacheDuration: this.CACHE_DURATION_MS }
        )
    }

    /**
     * Clears a specific associated users cache entry
     */
    public clearAssociatedUsersCache(customerNumber: string, installationNumber: string): void {
        const cacheKey = this.cache.createKey('private-associatedUsers', customerNumber, installationNumber)
        this.cache.clear(cacheKey)
    }

    /**
     * Clears all associated users cache entries
     */
    public clearAllAssociatedUsersCache(): void {
        this.cache.clearByPattern(/^private-associatedUsers:/)
    }

    public updatePrimaryContact(
        customerNumber: string,
        request: EditPrivateProfilePrimaryContactRequest
    ): Observable<any> {
        return this.profileService
            .apiProfilePrivateCustomerCustomerNumberPrimaryContactPut(customerNumber, request, 'response', true)
            .pipe(
                tap(() => {
                    // Invalidate installations and associated users cache after successful update
                    this.clearInstallationsCache()
                    this.cache.clearByPattern(new RegExp(`^private-associatedUsers:${customerNumber}:`))
                }),
                catchError((error: any) => {
                    this.handleError(error)
                    return of(EMPTY)
                })
            )
    }

    public subscribeToEasyAccount(customerNumber: string, subscribe: boolean): Observable<any> {
        const request: UpdateNemkontoStatusRequest = { nemkontoActive: subscribe }
        return this.profileService
            .apiProfilePrivateCustomerCustomerNumberNemkontoPut(customerNumber, request, 'response', true)
            .pipe(
                tap(() => {
                    // Invalidate installations and associated users cache after successful update
                    this.clearInstallationsCache()
                    this.cache.clearByPattern(new RegExp(`^private-associatedUsers:${customerNumber}:`))
                }),
                catchError((error: any) => {
                    this.handleError(error)
                    return of(EMPTY)
                })
            )
    }

    public subscribeToDigitalPost(customerNumber: string, subscribe: boolean): Observable<any> {
        const request: UpdateDigitalPostConsentRequest = { digitalPostActive: subscribe }
        return this.profileService
            .apiProfilePrivateCustomerCustomerNumberEboksPut(customerNumber, request, 'response', true)
            .pipe(
                tap(() => {
                    // Invalidate installations and associated users cache after successful update
                    this.clearInstallationsCache()
                    this.cache.clearByPattern(new RegExp(`^private-associatedUsers:${customerNumber}:`))
                }),
                catchError((error: any) => {
                    this.handleError(error)
                    return of(EMPTY)
                })
            )
    }

    public saveCprNumber(customerNumber: string, cpr: string): Observable<any> {
        const request: UpdateCustomerCprRequest = { cpr, consent: true }
        return this.profileService
            .apiProfilePrivateCustomerCustomerNumberCprPut(customerNumber, request, 'response', true)
            .pipe(
                tap(() => {
                    // Invalidate installations and associated users cache after successful update
                    this.clearInstallationsCache()
                    this.cache.clearByPattern(new RegExp(`^private-associatedUsers:${customerNumber}:`))
                }),
                catchError((error: any) => {
                    this.handleError(error)
                    return of(EMPTY)
                })
            )
    }

    private handleError(error: any) {
        RedirectTo.internalServerError(this.router, error)
    }
}
